import React, { useMemo } from 'react';
import { DownloadButton } from '@/pages/dashboard/components/Plugin/DownloadButton';
import type { IDownloadButtonProps } from '@/pages/dashboard/components/Plugin/DownloadButton/type';
import {
  buildPrDownloadPayload,
  buildPrBackendDownloadCsvFileName,
  getPrExcelDownloadMetricSegment,
  type IBuildPrDownloadPayloadOptions,
} from '../../_utils';

const PR_DOWNLOAD_DEFAULT_API = '/api/v2/dashboard/studio/pr/download';

/** Studio PR 表格「后端 Blob 导出」：`beforeRequest` 内统一调用 `buildPrDownloadPayload`。 */
export interface IPrDownloadButtonProps extends Omit<IDownloadButtonProps, 'config'> {
  fileNameEn: string;
  fileNameCn: string;
  /**
   * 若在 PR 侧已指定完整文件名（可含或不含 `.csv` / `.xlsx`），则覆盖默认规则；
   * 不传时：`PR_{游戏名}_{指标段}_{起始YYYYMMDD}_{结束YYYYMMDD}.csv`（见 `buildPrBackendDownloadCsvFileName`）。
   */
  downloadFileName?: string;
  /** 文件名中「指标段」文案（如 `Overview Articles`）；不传则由 `prDownloadTab` 映射（Media / Regions / …）。 */
  downloadFilenameSegment?: string;
  /** 对应 `_utils.buildPrDownloadPayload` 的 `tab` */
  prDownloadTab: string;
  prTableColumns: NonNullable<IBuildPrDownloadPayloadOptions['tableColumns']>;
  /** 默认 `/api/v2/dashboard/studio/pr/download` */
  downloadApi?: string;
}

const PrDownloadButton: React.FC<IPrDownloadButtonProps> = (props) => {
  const {
    prDownloadTab,
    prTableColumns,
    fileNameEn,
    fileNameCn,
    downloadFileName: downloadFileNameProp,
    downloadFilenameSegment,
    downloadApi = PR_DOWNLOAD_DEFAULT_API,
    ...rest
  } = props;

  const config = useMemo(
    () => ({
      backendDownload: {
        method: 'POST' as const,
        api: downloadApi,
        fileNameEn,
        fileNameCn,
        resolveDownloadFileName: (requestData: Record<string, unknown>) => {
          if (downloadFileNameProp !== undefined && downloadFileNameProp.trim() !== '') {
            return downloadFileNameProp.trim();
          }
          const segment =
            (downloadFilenameSegment && downloadFilenameSegment.trim()) ||
            getPrExcelDownloadMetricSegment(prDownloadTab);
          return buildPrBackendDownloadCsvFileName(requestData, segment);
        },
        beforeRequest: (requestData: Record<string, unknown>) =>
          buildPrDownloadPayload(requestData, {
            tab: prDownloadTab,
            tableColumns: prTableColumns,
          }),
      },
    }),
    [
      downloadApi,
      downloadFileNameProp,
      downloadFilenameSegment,
      fileNameCn,
      fileNameEn,
      prDownloadTab,
      prTableColumns,
    ],
  );

  return <DownloadButton {...rest} config={config} />;
};

export default PrDownloadButton;
